import React, {useEffect, useRef, useState} from 'react';
import {AlertCircle, CheckCircle, File, Save, Settings, Upload, X} from 'lucide-react';
import {getProjectData, saveProjectData, getGlobalData, saveGlobalData} from '../utils/projectStorage';
import './McodeTranslationSettings.css';

interface McodeTranslationSettingsData {
  generalInstructionsPrompt: string;
  dimensionsForTranslation: string;
}

const McodeTranslationSettings: React.FC = () => {
  const [settings, setSettings] = useState<McodeTranslationSettingsData>({
    generalInstructionsPrompt: `You are a translation expert who specializes in reframing content according to different Motivational Dimensions. Your task is to take the input text and rephrase it to appeal strongly to the specific Motivational Dimension mentioned in the instructions.

If you don't get given the Dimension, ask for it. You need it to do anything.

## Guidelines:
1. Maintain the core message and information of the original text
2. Adjust the language, tone, examples, and emphasis to appeal to the specific Motivational Dimension
3. Use vocabulary and framing that resonates with the dimension's values and motivations
4. Keep the translation natural and authentic - it should not feel forced or artificial
5. Preserve the format, length, and structure of the original text as much as possible
6. Do not add unnecessary information or remove important details
7. Always use professional language and maintain the original level of formality
8. If the text contains technical terms, preserve them in the translationYour goal is to make the content more compelling and engaging to someone with the specified Motivational Dimension, while keeping the essence of the original message intact.

Instructions for translation:
1. Analyze the statement to understand its core purpose and goals
2. Reshape the message specifically for the [dimension] dimension, considering their motivations, communication style, fears, and values
3. Explain the key changes made and why they would resonate with that dimensionYour job is to use all the information you know about translating the language of the statement into a new version of that statement for a person of the [dimension] dimension.

This isn't a template oriented process. This isn't a word or phrase replacement process.

Your job is to "understand" the point and goal of the message and then "shape" it for the [dimension] dimension.`,
    dimensionsForTranslation: `Understanding one's Motivations can be incredibly eye-opening. But it’s also challenging to talk about your MCode with others who don’t share the same Motivations as you. After all, not everyone is up to the task of memorizing the definitions and nuances of all 32 Motivations.

So in this document, we’ll take a closer look at each of the 8 MCode Dimensions, which offer an easier way to understand and talk about groups of related Motivations.

After processing more than a million achievement stories over the past 60 years, our team of behavioral scientists, researchers, and consultants observed that certain Motivations shared a number of common characteristics. So we’ve grouped related Motivations into eight larger families, which we call Dimensions.

For example, the Motivations that fall under the Visionary Dimension are generally about looking to the future. But each one gives off motivational energy in a very unique way, so it’s important to focus on both your individual motivations as well as your primary Dimension when interpreting your MCode. The Dimensions are simply a way of showing which themes share a common ‘DNA.’

You might ask, "Why do certain Motivations appear in more than one Dimension?"

Several motivational themes appear within multiple Dimensions. For example, the motivation to Identify Potential which appears in both the Visionary and Influencer Dimensions, can manifest differently based on the overarching Dimension it is associated with.

For an individual in the Visionary Dimension, recognizing potential often aligns with their forward-thinking and innovative nature. They might spot future possibilities and untapped opportunities that align with their creative and strategic thinking.

On the other hand, when Identify Potential is present in an Influencer, it often ties to their ability to see and cultivate the potential in people. They are adept at recognizing latent talents and strengths in others and guiding them towards personal and professional growth.

In both cases, the core motivation is the same, but the application and focus differ; Visionaries tend to focus on ideas and concepts, while Influencers emphasize people and relationships.

You might ask, "How does one utilizes their Dimensions in real life?"

Once you’ve identified your MCode Dimensions, you can start applying this understanding in various aspects of your life.

In Personal Growth:

Understanding your Dimensions helps in setting personal goals and aspirations that truly resonate with you. For example, if you are a Learner, you’ll likely find fulfillment in continuous education or exploring new hobbies.

In Professional Settings:

Your Dimensions can guide career choices and job roles. An Orchestrator will thrive in management roles, where organizing and directing are key components of the job.

In Relationships:

Recognizing your Dimensions can improve how you communicate and connect with others. If you’re an Influencer, you might naturally take on mentoring roles or be the one who initiates changes and new ideas in your social circles.

In Team Dynamics:

Understanding not just your own but also your team members’ Dimensions can greatly enhance teamwork. This insight allows for better allocation of roles and responsibilities, ensuring that everyone’s tasks align with their intrinsic motivations.

With that as background, here are the 8 Dimensions of Motivation, with a brief description for each.

----------------

The 8 MCode Dimensions:

----------------

Achiever Dimension

Primary Goal:
To be the best and make the most of every opportunity. Being recognized as distinctive and standing out in a way that elicits a response from others.

Understanding the Achiever:
Achievers are high performers at their jobs and in their lives. They seek to play critical and standout roles to bring success to organizations, and will often raise the bar on quality and standards within a team through their own personal ambitions and achievements.

- The work environments that suit Achievers best are those that allow them to be key and central, and where there is opportunity for them to lead and give direction. The culture and company vision must value high performers and reward them for meeting their goals that exceed expectations.
- An Achiever will not thrive in work environments where everything is collaborative, the tasks are mundane and they receive little recognition of their unique contributions. They will not thrive in an organization where the products and services are not “A-level”.
- Achievers should be self-aware of their tendency to dismiss or diminish the contributions of others and not acknowledge how everyone needs a time to shine. They must be careful to not become overly focused on personal performance because it can lead to unrealistic standards.
- If you are on a team with an Achiever they will be ambitious and goal-oriented, due to their high achieving and self-motivated attitude they tend to cause a spirit of healthy competition in a team or organization, inspiring others to be their very best.

Voice:
Achievers are strategic and assertive communicators who focus on results and motivating others with their ambition and high standards.

Brand:
Achievers are known for excellence and strong leadership. They set the example and inspire others with their drive, high performance, and accomplishments.

Fears:
Underperformance, lack of recognition or reward, being held back, and environments that don’t challenge or highlight their unique contributions.

Achievers at their best:
At their best, Achievers have autonomy in decision-making, receive recognition of their unique talents and efforts, and experience opportunities to lead, set new standards, and fully commit and excel in every endeavor.

Work at its best:
At its best, work for an Achiever involves challenging roles with clear goals, opportunities to lead others, and an environment that acknowledges and rewards their distinctive contributions, accomplishments, and impact.

Life at its best:
At its best, life for an Achiever centers around continuous personal growth, achieving significant milestones, and having impactful experiences that drive personal satisfaction and positively influence and inspire those around them.

Notable Achievers:
- Cher
- Elon Musk
- Beyoncé
- Michael Phelps

You know you’re an Achiever when you:
- Set a high bar and reach it.
- Impress and inspire.
- Accomplish great things.
- Seize every opportunity.
- Differentiate to stand apart.
- Enjoy healthy competition.

Motivations within the Achiever Dimension: 
- Excel
- Mastery
- Evoke Recognition
- Be Unique
- Be Key
- Maximize

--------------------

Driver Dimension

Primary Goal: 
To move things forward at all costs, meet challenges head on, and overcome obstacles to press on to the finish line.

Understanding the Driver:
Drivers look for every opportunity to advance toward established goals and motivate the people around them to do the same. They are not intimidated by difficult circumstances and typically thrive in challenging situations. They have the ability to stay calm under pressure and be tenacious despite resistance or repeated failures.

- Drivers thrive in work environments that are goal-oriented and with plans are well-defined. They expect clear communication about next steps and deadlines, and are at their best when the work is difficult, challenging, or demanding, or it presents “impossible missions.” It is important to them to work for a company or organization where perseverance and persistence are rewarded.
- The work that Drivers do must come to an end or have a completion date or it will not suit them well. Positions where there is little room for personal advancement and roles or assignments that are too simple or mundane cause Drivers to lose motivation and do not incite optimal performance.
- Drivers who do not find work that provides advancement, challenges, and opportunities to bring significant projects to completion, will be frequently discontent or bored. On the contrary, if they allow stress to build up or take on too much they could become irritable with others and be prone to burnout. Balancing their need for the rush of overcoming obstacles with a healthy amount of pressure is key to long-term success for the Driver.
- If you are on a team with a Driver you can expect them to be prepared, resilient, confident, courageous, gritty, bold and the voice of reason in the midst of chaos. They are dedicated and diligent to run the race full of vigor, settling for nothing less than victory. You can count on them when it looks like the chips are down to rise above and help drive a team to accomplish all that was set before them.

Voice:
Drivers are direct, decisive communicators who focus on progress and solutions. They clearly articulate goals and the steps to achieve them.

Brand:
Drivers are resilient, determined, goal-oriented leaders who thrive under pressure. They embrace challenges and drive meaningful results.

Fears:
Stagnation, delays beyond their control, environments that don’t offer challenges or opportunities for advancement, and a lack of clear goals.

At their best:
At their best, Drivers seek opportunities that challenge their resilience and push them outside your comfort zone, situations that allow for decisive action and progress, and the autonomy to tackle and overcome significant obstacles.

Work at its best:
At its best, work for a Driver requires environments that are goal-oriented and challenging, where clear objectives and deadlines drive their motivation and action, and persistence and determination are recognized and rewarded.

Life at its best:
At its best, life for a Driver is most fulfilling and satisfying when they’re overcoming challenges, achieving bold goals, and engaging in pursuits that test their limits and allow for continuous personal and professional growth.

Notable Drivers:
- Leonardo DiCaprio
- Indra Nooyi
- Oprah Winfrey
- Brett Favre

You know you’re a Driver when you:
- Set bold, audacious goals.
- Track and celebrate their progress.
- Take a fearless approach.
- Overcome obstacles.
- Pursue ambitions relentlessly.
- Channel determination.

Motivations within the Driver Dimension: 
- Advance
- Finish
- Meet the Challenge
- Overcome
- Gain Ownership

--------------------

Influencer Dimension

Primary Goal:
To see possibilities, shape and impact the people, products, and systems around them, and persuade others to join their efforts.

Understand the Influencer:
Influencers desire to help others reach their goals and realize their potential, seeing the gold in others and helping them be all they were created to be. Influencers will advance civilization through inspiration and practical dreaming, being able to build and create something from nothing.

- An optimal work environment for Influencers is where there are a lot of new prospects and possibility-thinking is needed. They seek to have a culture that truly brings out the best in people and where mentoring and personal growth is highly valued.
- Influencers struggle in work environments where ideas are only talked about and plans never leave the whiteboard. They need to be given freedom to share their ideas and see them implemented.
- Influencers need to keep their tendency to over-influence in check and be careful of talking too often or in pushy ways about how others need to get onboard with their ideas. They must also keep in mind that others have important opinions and learn to create space for them to share their ideas too.
- Influencers must be cautious of how they make investments and spend resources, as they tend to see potential in everything, even when there is little hope for something of value to be produced. To avoid disappointment and burnout they must also learn to find contentment in the ordinary and in the moment, not banking their happiness on possibilities that may never materialize.
- If you are on a team with an Influencer, you can expect them to be charismatic and inspiring. They will often captivate people with their visions and compelling ideas that help to engage and empower others in dynamic and transformative ways.

Voice:
Influencers are charismatic, passionate, infectious communicators who share their vision and engage others with a persuasive, inspiring style.

Brand:
Influencers have an ability to inspire and lead others. Often seen as visionaries, they turn ideas into action and foster growth in people and projects.

Fears:
Unimplemented ideas, environments that stifle creativity or ignore potential, and the possibility of failing to gain buy-in, inspire, and mobilize others.

At their best:
At their best, Influencers are in spaces where ideas are embraced and implemented, their leadership flourishes, and their talent for inspiring and empowering others is recognized and harnessed as they achieve their highest potential.

Work at its best:
At its best, work for an Influencer is a dynamic environment filled with new possibilities, where innovative, visionary thinking is celebrated, mentoring is a key focus, and creative ideas are not only welcomed but brought to life.

Life at its best:
At its best, life for an Influencer is a series of transformative, enriching, experiences, where they can inspire and connect with others while pursuing ambitious goals and creating a deeply fulfilling and satisfying personal journey.

Notable Influencers:
- Matthew McConaughey
- Warren Buffett
- Michelle Obama
- Simone Biles

You know you’re an Influencer when you:
- See potential and possibilities.
- Have life-changing ideas.
- Impact and transform lives.
- Make dream a reality.
- Help others reach their potential.
- Embrace creative thinking.

Motivations within the Influencer Dimension: 
- Persuade
- Make an Impact
- Identify Potential
- Develop

--------------------

Learner Dimension Landing Page

Primary Goal: To discover, understand, and increase your knowledge. To strive to comprehend, then demonstrate, and live out your newly acquired proficiencies.

Understand the Learner:
Learners are driven to gain cutting edge knowledge and discover new techniques. They push the boundaries of what is known and seek to be experts in their respective fields. Simply gaining knowledge is not enough for them, they desire to share what they have learned with others, fueling imaginations and inspiring the minds of the world around them.

- Learners thrive in work environments where a high degree of knowledge or expertise is required and they will have opportunities to teach, demonstrate or write about the wisdom they have. They function at their best when there is freedom to explore new ideas and research is the core of their work responsibilities.
- Learners must be able to have some independence or autonomy to seek out new information and have room for personal development to be truly successful. They do not flourish in work environments where their expertise and hunger for new knowledge is not valued. They lose motivation if their work is too prescriptive, simple, or shallow, or they are forced to advance with little time spent doing research.
- Learners require time and space to gain information about their work. They do not enjoy being forced to collaborate beyond their need for teaching or modeling what they know. Without ample time to dive deep into new areas of knowledge and having a platform to express or demonstrate what they have learned, Learners become frustrated and do not perform at their best.
- If you are on a team with a Learner you can expect them to be curious and inquisitive, always observant of their surroundings and very perceptive to new insights. They are thoughtful, analytical and reflective. When you are in need of new and innovative ideas, Learners will be there with the answers, or at least, know where to find them.

Voice:
Learners are analytical, reflective communicators who simplify complex concepts and share insights and knowledge in a thoughtful, inquisitive way.

Brand:
Learners have deep curiosity, expertise, and a commitment to continuous learning. They are respected, knowledgeable, and innovative thinkers.

Fears:
Stalled personal growth, sticking with the status quo, environments that undervalue their expertise, and the inability to explore or share new knowledge.

At their best:
At their best, Learners constantly evolve and excel. They have autonomy to explore new fields and ideas, access to resources for deepening their knowledge, and platforms to demonstrate and share their findings with others.

Work at its best:
At its best, work for a Learner involves an environment that highly values expertise and continuous learning, provides opportunities for research, discovery, and teaching, and allows for extensive growth and sharing of knowledge.

Life at its best:
At its best, life for a Learner is an ongoing journey of discovery, filled with opportunities for intellectual growth, sharing insights, and inspiring others, leading to a deep sense of personal and professional fulfillment, engagement, and joy.

Notable Learners:
- Johnny Depp
- Madame Curie
- Albert Einstein
- Serena Williams

You know you’re a Learner when you:
- Embrace continuous curiosity.
- Become experts in your field.
- Teach the world what you know.
- Seek intellectual growth.
- Experiment with new techniques.
- Research, distill, and share.

Motivations within the Learner Dimension: 
- Comprehend and Express
- Demonstrate Learning
- Explore
- Mastery

--------------------

Optimizer Dimension Landing Page

Primary Goal: To establish new and enhance existing systems, operations, and efficiencies through improving, fine-tuning, fixing, organizing, and solving problems.

Understand the Optimizer:
Optimizers are precise and reliable, often setting the standard for social justice and change. They are attentive, see details others miss, and seek to improve and bring about desired results to problematic situations. Optimizers have a desire to help others understand the requirements for success, and deliver adequate performances to the standard set before them.

- A healthy work environment for Optimizers is one that provides detailed expectations and requirements, and where fine-tuning and attention-to-detail skills are primary. They thrive in organizations looking to improve, repair, or revamp their services and products.
- Optimizers do not perform well in organizations where products and processes are not already established or where there is a lot of blank-slate work that needs to be done. They can feel lost and overwhelmed if not given defined expectations and clear direction from management.
- Optimizers must be aware of their tendency to be overly strict or critical with company rules or standards to avoid discouraging or reprimanding co-workers. They also must recognize their need to fix something, as to not create problems where none exist.
- If you are on a team with an Optimizer they will be analytical and can often be found in problem-solving mode. They continuously bring improvement and efficiency to every area they touch.

Voice:
Optimizers are precise, detail-oriented communicators who focus on practical solutions, improvements, and articulating exact requirements and standards.

Brand:
Optimizers are detail-oriented, meticulous, and reliable. They are efficient problem-solvers who improve and fine-tune systems and processes.

Fears:
Unclear expectations, disorganized environments, flying by the seat of their pants, and situations where their skills are underutilized or unappreciated.

At their best:
At their best, Optimizers are in a role that values their meticulous approach, clearly articulates guidelines, offers the ability to refine systems, and ensures their contributions lead to tangible and meaningful improvements.

Work at its best:
At its best, work for an Optimizer involves well-defined expectations, a focus on enhancing systems and processes, and opportunities to use their precision and attention to detail for organizational improvement and efficiency.

Life at its best:
At its best, life for an Optimizer is filled with opportunities to apply problem-solving skills, make things work better and more efficiently, and enhance personal and professional environments to bring about accomplishment and satisfaction.

Notable Optimizers:
- Duane Johnson
- James Dyson
- Marie Kondo
- Lindsey Vonn

You know you’re an Optimizer when you:
- Are effective and efficient.
- Improve what already exists.
- Fix what is broken.
- Meet critical requirements.
- Embrace systems and processes.
- Pay attention to detail.

Motivations within the Optimizer Dimension: 
- Systematize
- Meet Requirements
- Make it Work
- Do it Right
- Improve

--------------------

Orchestrator Dimension

Primary Goal: To bring definition, direction, and oversight to people, processes, and efforts. To lead and be key in developing strategies and implementing them.

Understand the Orchestrator:
Orchestrators bring separate or disconnected things together, establishing the practical steps others failed to take and directing people to accomplish tasks in organized and orderly ways. They leave a legacy that impacts future generations and help to provide assurance or hope in the face of uncertainty.

- The ideal work environment for an Orchestrator allows them to organize and oversee people and processes. They want to lay foundations and establish practical strategies for how things are done within the organization and thrive when their expertise is needed and valued.
- Orchestrators do not thrive in work environments where they are confined to working on and improving what already exists. They will experience anxiety if the organization allows projects to go unfinished or unresolved and will not be motivated within cultures where roles are ambiguous and there is no room for leadership opportunities.
- With a desire to have direct involvement in every stage of a project, Orchestrators must resist over extending their role and remain open to allowing others to make their own way, even if it results in failure. They must also be aware of their own stress level and how much they choose to take on as their personal responsibility.
- If you are on a team with an Orchestrator they will be decisive and proactive, and show up as a natural-born leader. They are planners with a system for everything, and are truly the glue that holds an organization together.

Voice:
Orchestrators are decisive, strategic, clear communicators who aim to create order through the articulation of practical steps for achieving goals.

Brand:
Orchestrators are leaders with strategic planning skills and the ability to unify teams and processes. They lay strong foundations and drive outcomes.

Fears:
Ambiguity in roles, unstructured environments, inefficiency, and situations where their leadership and planning skills are ignored or undervalued.

At their best:
At their best, Orchestrators lead and make impactful decisions in environments that value their planning and organizational skills and offer the autonomy to bring together people and processes to achieve common goals.

Work at its best:
At its best, work for an Orchestrator involves opportunities that allow for strategic oversight and organization and situations where their leadership is crucial in establishing and implementing practical systems and strategies for success.

Life at its best:
At its best, life for an Orchestrator fully utilizes their strategic and leadership skills in personal and professional spheres, bringing about accomplishment and legacy through their ability to organize and direct efforts toward meaningful outcomes.

Notable Orchestrators:
- Reese Witherspoon
- Sheryl Sandberg
- Bill Graham
- Michael Jordan

You know you’re an Orchestrator when you:
- Organize and bring order to chaos.
- Establish and build foundations.
- Oversee, lead, and advise.
- Help others accomplish tasks.
- Resolve issues and finish projects.
- Make and execute plans.

Motivations within the Orchestrator Dimension: 
- Systematize
- Be Key
- Architect
- Take Charge
- Establish
- Develop

--------------------

Relator Dimension

Primary Goal: To inspire and connect with others, make meaningful contributions to a team, and truly believe more can be accomplished together than alone.

Understand the Relator:
Relators can be found bringing people together and helping clarify the vision or mission for a group to elevate team connection and collaboration. They encourage and model company or community values, demonstrate humility by putting others first, and have a special way of inspiring others with their selfless and sacrificial contributions.

- Relators thrive in work environments that demonstrate care and genuine concern for employees and customers through a service-based culture. They excel when they know their contribution to a team will make a real difference in the world, and perform at their best when there are high standards established for performance, and their organization’s brand and products are seen as best in class.
- Relators need work that incorporates teamwork. Roles that are primarily independent are not healthy or fruitful for Relators. Similarly, when high standards and outstanding achievements are not defined or communicated, Relators are often left feeling frustrated and unmotivated.
- Work that does not promote meeting the needs of others or accomplishing tasks for the “greater good” can cause Relators to become discouraged and underperform. Relators must maintain healthy boundaries and make time for self-care, so as not to lose themselves within the identity of a team.
- If you are on a team with a Relator you can expect them to be an empathetic and compassionate listener and act as the barometer for the emotional atmosphere of the office. They are instinctually sensitive to the needs of others and will be highly communicative and genuine in their relationships.

Voice:
Relators are empathetic, sincere, collaborative communicators who listen attentively and articulate the collective vision and needs of a group or team.

Brand:
Relators are service-oriented and have a genuine concern for others. Their ability to inspire and unite teams makes them the heart and soul of any group.

Fears:
Loss of personal identity, environments that lack teamwork and shared goals, situations where their contributions to collective well-being are undervalued.

At their best:
At their best, Relators need environments that foster strong teamwork, recognize and value their empathetic nature and ability to connect with others, and allow them to contribute meaningfully to the team’s success.

Work at its best:
At its best, work for a Relator involves a culture that values service and team collaboration, where their contributions are meaningful and make a real difference, and where high standards of performance are aligned with the greater good.

Life at its best:
At its best, life for a Relator is rich with deep, genuine connections, opportunities to make a positive impact through service, a balanced focus on both collective and personal well-being, and a sense of fulfillment and purpose.

Notable Relators:
- Dolly Parton
- Sir Richard Branson
- Mother Teresa
- Roger Federer

You know you’re a Relator when you:
- Serve others and put them first.
- Believe teamwork makes the dream work.
- Inspire collaboration.
- Seek opportunities to make a difference.
- Strive for meaningful connections.
- Listen to understand and show empathy.

Motivations within the Relator Dimension: 
- Meet Needs
- Collaborate
- Make the Team
- Persuade

--------------------

Visionary Dimension

Primary Goal: To envision possibilities, ideas, and opportunities, and make them happen with design and creative strategies. To inspire and bring to life what others dream about.

Understand the Visionary:
The creative and innovative thinking of Visionaries bring traction and progress to the ideas and dreams of others. They typically drive social change, make life richer, and model and integrate their high morals and personal values in their daily interactions. Visionaries are known for seeing the possibilities in ideas and people that would otherwise go unrecognized.

- Visionaries thrive in work environments where ideas and dreams can be expressed in creative and artistic ways and become reality. They require an atmosphere where potential, possibilities, and ambition are embraced, as they seek to execute, innovate, and see evidence of their visions coming to fruition.
- Work environments that provide the freedom to be original, create, and incorporate their mission and values are a must for Visionaries. A lack of ongoing training or mentoring will leave them uninspired and they will not function at their full potential when their innovative ideas and out-of-the-box thinking is discouraged.
- Visionaries are prone to thinking their designs and ideas are best and can struggle to embrace critique from others. It is important for them to be aware of their high ideals, remain grounded in reality, not become distracted by endless possibilities and potential, and finish the work at hand.
- If you are on a team with a Visionary you can expect them to be imaginative, creative, inspirational, and innovative. They are idealists and dreamers but also strategists in making the impossible a reality and bringing transformation to environments and people.

Voice:
Visionaries are creative, imaginative, innovative, inspirational communicators who can articulate future possibilities and transformative visions.

Brand:
Visionaries are defined by creativity, originality, and the ability to see and realize potential in ideas and people. They are pioneers and change-makers.

Fears:
Dead ends, a lack of opportunities for personal and professional growth, environments that stifle creativity and discourage innovative thinking.

At their best:
At their best, Visionaries need to be in spaces that nurture creative thinking and environments that support their ambitious goals, value their unique perspective, and enable them to bring transformative ideas to life.

Work at its best:
At its best, work for a Visionary involves a space where creativity and innovation are not just allowed but encouraged, where ideas can take flight, and where there is freedom to infuse their values and vision into meaningful projects.

Life at its best:
At its best, life for a Visionary is filled with opportunities for dreaming and creative expression, the realization of ambitious projects, continuous personal growth, and the fulfilling experience of turning dreams into realities.

Notable Visionaries:
- Meryl Streep
- Steve Jobs
- Walt Disney
- Megan Rapinoe

You know you’re a Visionary when you:
- Inspire, imagine, and dream.
- Cast a vision for the future.
- Make the impossible possible.
- Drive strategic transformation.
- Embrace passion and creativity.
- Live out your morals and values.

Motivations within the Visionary Dimension: 
- Design
- Experience the Ideal
- Realize the Vision
- Identify Potential`
  });
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load existing settings
    const projectData = getProjectData('mcode-translation');
    if (projectData.settings) {
      setSettings({
        generalInstructionsPrompt: projectData.settings.generalInstructionsPrompt || '',
        dimensionsForTranslation: projectData.settings.dimensionsForTraslantion || ''
      });
    }
  }, []);

  const handleInputChange = (field: keyof McodeTranslationSettingsData, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setMessage('');
    setMessageType('');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['.txt', '.md', '.json'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      setMessage('Please upload a .txt, .md, or .json file');
      setMessageType('error');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage('File size must be less than 5MB');
      setMessageType('error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // Save to global data instead of project-specific
      const globalData = getGlobalData();
      globalData.motivationalDimensionsFile = {
        name: file.name,
        content,
        lastModified: file.lastModified,
        size: file.size
      };
      saveGlobalData(globalData);
      setMessage('File uploaded successfully to global storage');
      setMessageType('success');
    };
    reader.readAsText(file);
  };

  const removeFile = () => {
    // Remove from global data
    const globalData = getGlobalData();
    globalData.motivationalDimensionsFile = undefined;
    saveGlobalData(globalData);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setMessage('File removed successfully');
    setMessageType('success');
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Get current project data and update settings
      const projectData = getProjectData('mcode-translation');
      projectData.settings = settings;
      
      // Create ingested content using global motivational dimensions file
      const globalData = getGlobalData();
      const mcodeDimensionsContent = globalData.motivationalDimensionsFile?.content || '';

      projectData.ingestedContent = `You are an MCODE Translation AI Assistant specialized in translating motivational dimensions and coaching content across languages and cultural contexts.

General Instructions: ${settings.generalInstructionsPrompt || 'Translate and analyze MCODE motivational dimensions across languages and cultural contexts.'}

${mcodeDimensionsContent ? `## Motivational Dimensions Framework
The following is the complete MCode Motivational Dimensions framework that you must use when providing translation guidance:

${mcodeDimensionsContent}

Please ensure you fully understand and apply these motivational dimensions when tailoring your responses to specific personality types.` : 'No MCODE dimensions file provided yet.'} You must provide accurate, culturally-sensitive translations and analyses that:
- Maintain the integrity and meaning of MCODE dimensional concepts
- Adapt content appropriately for different languages and cultural contexts
- Preserve the psychological and motivational framework principles
- Offer culturally relevant examples and applications
- Consider linguistic nuances and cultural sensitivities

Here's the information you should use about each dimension when translating or analyzing content:
${settings.dimensionsForTranslation || 'No specific dimensions provided yet.'}

When responding to translation questions, focus on the specified motivational dimension and provide practical guidance for cross-cultural adaptation while maintaining the core MCODE principles.`;
      
      // Save to storage
      saveProjectData('mcode-translation', projectData);
      
      setMessage('Settings saved and processed successfully!');
      setMessageType('success');
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
      setMessageType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="mcode-translation-settings">
      <div className="settings-header">
        <div className="header-title">
          <Settings size={24} />
          <h1>MCODE Translation Settings</h1>
        </div>
        <p>Configure settings for MCODE Translation functionality</p>
      </div>

      <div className="settings-form">
        <div className="form-section">
          <label htmlFor="generalInstructionsPrompt">General Instructions Prompt</label>
          <textarea
            id="generalInstructionsPrompt"
            placeholder="Enter general instructions for MCODE translation and analysis..."
            value={settings.generalInstructionsPrompt}
            onChange={(e) => handleInputChange('generalInstructionsPrompt', e.target.value)}
            rows={6}
          />
          <p className="field-description">
            These instructions will guide how AI translates and analyzes MCODE dimensions across languages.
          </p>
        </div>

        <div className="form-section">
          <label htmlFor="dimensionsForTraslantion">Dimensions for Translation</label>
          <textarea
              id="dimensionsForTranslation"
              placeholder="Enter general instructions for MCODE translation and analysis..."
              value={settings.dimensionsForTranslation}
              onChange={(e) => handleInputChange('dimensionsForTranslation', e.target.value)}
              rows={6}
          />
        </div>

        <div className="form-section">
          <label htmlFor="mcodeDimensionsFile">MCode Dimensions File</label>

          {(() => {
            const globalData = getGlobalData();
            return globalData.motivationalDimensionsFile ? (
              <div className="file-display">
                <div className="file-info">
                  <File size={20} />
                  <div className="file-details">
                    <span className="file-name">{globalData.motivationalDimensionsFile.name}</span>
                    <span className="file-meta">
                      {formatFileSize(globalData.motivationalDimensionsFile.size)} •
                      {new Date(globalData.motivationalDimensionsFile.lastModified).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="remove-file-btn"
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="file-content-preview">
                  <strong>File Content Preview:</strong>
                  <div className="content-preview">
                    {globalData.motivationalDimensionsFile.content.substring(0, 200)}
                    {globalData.motivationalDimensionsFile.content.length > 200 && '...'}
                  </div>
                </div>
              </div>
            ) : (
            <div className="file-upload">
              <input
                ref={fileInputRef}
                type="file"
                id="mcodeDimensionsFile"
                accept=".txt,.md,.json"
                onChange={handleFileUpload}
                className="file-input"
              />
              <label htmlFor="mcodeDimensionsFile" className="file-upload-label">
                <Upload size={20} />
                <span>Upload MCode Dimensions File</span>
                <span className="file-types">Supported: .txt, .md, .json (max 5MB)</span>
              </label>
            </div>
          );
          })()}

          <p className="field-description">
            Upload a file containing MCODE dimensional definitions, translations, or reference materials. This file will be shared across all projects.
          </p>
        </div>

        {message && (
          <div className={`message ${messageType}`}>
            {messageType === 'success' ? (
              <CheckCircle size={16} />
            ) : (
              <AlertCircle size={16} />
            )}
            <span>{message}</span>
          </div>
        )}

        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="save-button"
        >
          <Save size={18} />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default McodeTranslationSettings;